import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  CheckCircle,
  CircleNotch,
  Eye,
  EyeSlash,
  Lock,
  WarningCircle
} from '@phosphor-icons/react';
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error('Invalid password reset link');
      setTimeout(() => navigate('/forgot-password'), 2000);
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const passwordRequirements = [
    { label: 'At least 12 characters', test: (pwd) => pwd.length >= 12 },
    { label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { label: 'Contains number', test: (pwd) => /[0-9]/.test(pwd) },
    { label: 'Contains special character', test: (pwd) => /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'~`]/.test(pwd) }
  ];

  const commonPasswords = [
    'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', '1234567', 
    'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou', 'master', 'sunshine',
    'ashley', 'bailey', 'passw0rd', 'shadow', '123123', '654321', 'superman',
    'qazwsx', 'michael', 'football', 'welcome', 'jesus', 'ninja', 'mustang',
    'password1', '123456789', '12345', 'password123'
  ];

  const isCommonPassword = (pwd) => {
    const lowerPwd = pwd.toLowerCase();
    return commonPasswords.some(common => lowerPwd.includes(common));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const failedRequirements = passwordRequirements.filter(req => !req.test(formData.password));
      if (failedRequirements.length > 0) {
        newErrors.password = 'Password does not meet all requirements';
      } else if (isCommonPassword(formData.password)) {
        newErrors.password = 'Please avoid common passwords';
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          new_password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      toast.success('Password reset successfully! Redirecting to login...');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      toast.error(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <SEO 
        title="Reset Password - Man & Machine AI Opportunity Analyzer"
        description="Create a new password for your account"
      />
      <LandingHeader />

      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-[#6366F1]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Reset Password
              </h1>
              <p className="text-[#A0AEC0]">
                Create a new strong password for your account
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.password ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#6366F1]'
                    } transition-all`}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718096] hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2 space-y-1">
                    {passwordRequirements.map((req, index) => {
                      const isValid = req.test(formData.password);
                      return (
                        <div key={index} className="flex items-center gap-2 text-xs">
                          <CheckCircle 
                            size={14} 
                            className={isValid ? 'text-[#14F195]' : 'text-[#718096]'}
                          />
                          <span className={isValid ? 'text-[#14F195]' : 'text-[#718096]'}>
                            {req.label}
                          </span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle 
                        size={14} 
                        className={!isCommonPassword(formData.password) ? 'text-[#14F195]' : 'text-[#718096]'}
                      />
                      <span className={!isCommonPassword(formData.password) ? 'text-[#14F195]' : 'text-[#718096]'}>
                        No common passwords
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.confirmPassword ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#6366F1]'
                    } transition-all`}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718096] hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-[#EF4444] flex items-center gap-1">
                    <WarningCircle size={14} />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-[#6366F1] hover:bg-[#5558E6] mt-6 disabled:opacity-50 transition-colors"
              >
                <span className="flex items-center justify-center gap-2 py-4 text-white font-bold text-lg">
                  {isSubmitting ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </span>
              </button>
            </form>

            {/* Security Note */}
            <div className="mt-6 p-4 rounded-xl bg-[rgba(99,102,241,0.1)] border border-[#6366F1]/20">
              <p className="text-xs text-[#A0AEC0]">
                <strong className="text-white">Security Tip:</strong> Use a unique password that you don't use 
                for other accounts. Consider using a password manager to generate and store strong passwords.
              </p>
            </div>
          </motion.div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPassword;
